import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import { parseEmails, people, TIER_OPTIONS } from './format';
import { useAddTeamMembers } from './useAccounts';

interface AddMembersDialogProps {
  teamId: string;
  teamName: string;
  onClose: () => void;
}

export function AddMembersDialog({
  teamId,
  teamName,
  onClose,
}: AddMembersDialogProps) {
  const [text, setText] = useState('');
  const [tier, setTier] = useState<string>('plus');
  const addMembers = useAddTeamMembers(teamId);
  const toast = useToast();
  const emails = parseEmails(text);

  const handleAdd = async () => {
    try {
      await addMembers.mutateAsync({ emails, tier });
      toast.success(
        'Invites sent',
        `${people(emails.length)} invited to ${teamName}.`
      );
      onClose();
    } catch (e) {
      toast.error(
        'Could not add members',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add members to {teamName}</DialogTitle>
          <DialogDescription>
            Each person gets an invite email and a seat on the team.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          rows={6}
          placeholder="Paste emails, separated by commas, spaces or new lines"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Select value={tier} onChange={(e) => setTier(e.target.value)}>
          {TIER_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option[0].toUpperCase() + option.slice(1)}
            </option>
          ))}
        </Select>
        {emails.length > 0 && (
          <p className="text-sm text-muted-foreground">
            Adds {emails.length} {tier} {emails.length === 1 ? 'seat' : 'seats'}{' '}
            to the team's subscription.
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={emails.length === 0 || addMembers.isPending}
          >
            {addMembers.isPending
              ? 'Adding...'
              : `Add ${people(emails.length)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
